import { Controller } from "@hotwired/stimulus";

// Multi-step Oscar site-generator wizard. Steps are plain divs toggled
// client-side; all answers live in ONE form submitted on the last step.
// Side-channel work happens mid-wizard via fetch:
//   - website preview  → auto-runs (debounced) as the URL is typed so
//                        the owner confirms "yes, that's us" — no
//                        second call-to-action on the step
//   - photo collection → fired when leaving the website step; their
//                        site's photos (vision-triaged) land in a
//                        library the Photos step offers for placement
//   - logo             → attached to branding immediately
//   - program photos   → attached to the image library; attachment ids
//                        ride along as program_images[<pid>][] inputs
export default class extends Controller {
  static targets = [
    "step", "dot", "back", "next",
    "logoPreview", "logoEmpty", "logoInput", "logoStatus",
    "darkLogoPreview", "darkLogoEmpty", "darkLogoStatus",
    "previewLoading", "previewPanel", "previewTitle", "previewDesc", "previewError",
    "library", "libraryGrid", "libraryStatus", "photoSlot",
  ];
  static values = {
    previewUrl: String,
    collectUrl: String,
    logoUrl: String,
    darkLogoUrl: String,
    programImageUrl: String,
    csrf: String,
    startStep: { type: Number, default: 0 },
  };

  // The website step's position (0-based) — collection kicks off when
  // the owner continues past it.
  WEBSITE_STEP = 1;

  connect() {
    this.index = Math.min(this.startStepValue, this.stepTargets.length - 1);
    this.collectedFor = null;
    this.selectedLibraryImage = null;
    this.websiteInput = this.element.querySelector("input[name='site_generation[website_url]']");
    this.websiteInput?.addEventListener("input", () => this.scheduleSitePreview());
    this.render();
  }

  next() {
    if (this.index === this.WEBSITE_STEP) this.collectSiteImages();
    this.index = Math.min(this.index + 1, this.stepTargets.length - 1);
    this.render();
  }

  back() {
    this.index = Math.max(this.index - 1, 0);
    this.render();
  }

  render() {
    this.stepTargets.forEach((step, i) => step.classList.toggle("hidden", i !== this.index));
    this.dotTargets.forEach((dot, i) => {
      dot.classList.toggle("bg-violet-600", i <= this.index);
      dot.classList.toggle("text-white", i <= this.index);
      dot.classList.toggle("border-violet-600", i <= this.index);
      dot.classList.toggle("text-gray-400", i > this.index);
    });
    this.backTarget.classList.toggle("invisible", this.index === 0);
    // The last step carries its own Generate button — hide Continue via
    // style so it can't lose a specificity fight with `inline-flex`.
    this.nextTarget.style.display = this.index === this.stepTargets.length - 1 ? "none" : "";
  }

  // ── Step 1: logo ────────────────────────────────────────────────

  async uploadLogo(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Instant local preview; the server round-trip only persists it.
    this.logoPreviewTarget.src = URL.createObjectURL(file);
    this.logoPreviewTarget.classList.remove("hidden");
    if (this.hasLogoEmptyTarget) this.logoEmptyTarget.classList.add("hidden");
    this.logoStatusTarget.textContent = "Uploading…";

    const body = new FormData();
    body.append("logo", file);

    const data = await this.post(this.logoUrlValue, body);
    if (data?.ok) {
      this.logoStatusTarget.textContent = "Saved — looking sharp.";
    } else {
      this.logoStatusTarget.textContent = data?.error || "Upload failed — try a PNG or JPEG.";
    }
  }

  // Optional dark-background logo variant — attached to the image
  // library; the id rides the final submit so the generation wires the
  // nav's two states (dark version over the hero, standard after
  // scroll).
  async uploadDarkLogo(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Instant local preview — the tile shouldn't wait on (or depend on)
    // the server round-trip to show the picked file.
    this.darkLogoPreviewTarget.src = URL.createObjectURL(file);
    this.darkLogoPreviewTarget.classList.remove("hidden");
    this.darkLogoEmptyTarget.classList.add("hidden");
    this.darkLogoStatusTarget.textContent = "Uploading…";

    const body = new FormData();
    body.append("logo", file);

    const data = await this.post(this.darkLogoUrlValue, body);
    if (data?.ok) {
      this.darkLogoStatusTarget.textContent = "Saved — the nav will use it over your hero.";

      const form = this.element.querySelector("form");
      let hidden = form.querySelector("input[name='dark_logo_attachment_id']");
      if (!hidden) {
        hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "dark_logo_attachment_id";
        form.appendChild(hidden);
      }
      hidden.value = data.attachment_id;
    } else {
      this.darkLogoStatusTarget.textContent = data?.error || "Upload failed — try a PNG or JPEG.";
    }
  }

  // ── Step 2: website (auto preview) ──────────────────────────────

  scheduleSitePreview() {
    clearTimeout(this.previewTimer);
    this.previewPanelTarget.classList.add("hidden");
    this.previewErrorTarget.classList.add("hidden");
    this.previewTimer = setTimeout(() => this.previewSite(), 900);
  }

  async previewSite() {
    const url = this.websiteInput?.value?.trim();
    if (!url || !/^https?:\/\/.+\../.test(url)) return;

    this.previewLoadingTarget.classList.remove("hidden");
    const data = await this.post(this.previewUrlValue, JSON.stringify({ url }), "application/json");
    this.previewLoadingTarget.classList.add("hidden");

    // The owner may have kept typing — ignore a stale response.
    if (url !== this.websiteInput?.value?.trim()) return;

    if (data?.ok) {
      this.previewTitleTarget.textContent = data.site_name || data.title || url;
      this.previewDescTarget.textContent = data.description || data.title || "";
      this.previewPanelTarget.classList.remove("hidden");
    } else {
      this.previewErrorTarget.textContent =
        data?.error || "Couldn't reach that site — double-check the address (you can still continue).";
      this.previewErrorTarget.classList.remove("hidden");
    }
  }

  // ── Photo collection (fires when leaving the website step) ──────

  async collectSiteImages() {
    const url = this.websiteInput?.value?.trim();
    if (!url || this.collectedFor === url) return;
    this.collectedFor = url;

    this.libraryStatusTarget.classList.remove("hidden");
    const data = await this.post(this.collectUrlValue, JSON.stringify({ url }), "application/json");
    this.libraryStatusTarget.classList.add("hidden");

    // Tell the final submit what was already collected (and from where)
    // so the generation job doesn't re-import the same photos.
    const form = this.element.querySelector("form");
    if (data?.images?.length) {
      const urlField = document.createElement("input");
      urlField.type = "hidden";
      urlField.name = "collected_images_url";
      urlField.value = url;
      form.appendChild(urlField);
      data.images.forEach((image) => {
        const field = document.createElement("input");
        field.type = "hidden";
        field.name = "collected_images[]";
        field.value = image.id;
        form.appendChild(field);
      });
    }

    (data?.images || []).forEach((image) => {
      // Photos the triage matched to a program go straight into that
      // program's first open slot; the rest wait in the library strip.
      const placed = image.program_id && this.autoPlace(image);
      if (!placed) this.addToLibrary(image);
    });
    this.libraryTarget.classList.toggle("hidden", this.libraryGridTarget.children.length === 0);
  }

  autoPlace(image) {
    const slot = this.photoSlotTargets.find(
      (candidate) => candidate.dataset.programId === String(image.program_id) && !candidate.dataset.filled
    );
    if (!slot) return false;
    this.fillSlot(slot, image);
    return true;
  }

  addToLibrary(image) {
    const thumb = document.createElement("button");
    thumb.type = "button";
    thumb.className = "w-14 h-14 rounded-md overflow-hidden border-2 border-transparent hover:border-violet-400 focus:outline-none";
    thumb.innerHTML = `<img src="${image.url}" class="w-full h-full object-cover" alt="">`;
    thumb.addEventListener("click", () => this.selectLibraryImage(thumb, image));
    this.libraryGridTarget.appendChild(thumb);
  }

  selectLibraryImage(thumb, image) {
    this.libraryGridTarget.querySelectorAll("button").forEach((b) => b.classList.remove("border-violet-500", "ring-1", "ring-violet-500"));
    if (this.selectedLibraryImage?.image === image) {
      this.selectedLibraryImage = null;
      return;
    }
    thumb.classList.add("border-violet-500", "ring-1", "ring-violet-500");
    this.selectedLibraryImage = { thumb, image };
  }

  // A slot click places the selected library photo; with nothing
  // selected it falls through to the file-upload dialog.
  slotClicked(event) {
    if (!this.selectedLibraryImage) return;
    event.preventDefault();

    const slot = event.currentTarget;
    if (slot.dataset.filled) return;
    this.fillSlot(slot, this.selectedLibraryImage.image);
    this.selectedLibraryImage.thumb.remove();
    this.selectedLibraryImage = null;
    this.libraryTarget.classList.toggle("hidden", this.libraryGridTarget.children.length === 0);
  }

  fillSlot(slot, image) {
    const img = document.createElement("img");
    img.src = image.url;
    img.className = "w-full h-full object-cover";
    slot.querySelector("svg")?.remove();
    slot.prepend(img);
    slot.classList.remove("border-dashed");
    slot.dataset.filled = "true";

    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = `program_images[${slot.dataset.programId}][]`;
    hidden.value = image.id;
    this.element.querySelector("form").appendChild(hidden);
  }

  // ── Program photo uploads ───────────────────────────────────────

  async uploadProgramImage(event) {
    const input = event.target;
    const file = input.files[0];
    if (!file) return;

    const slot = input.closest("label");
    slot.classList.add("animate-pulse");

    const body = new FormData();
    body.append("program_id", input.dataset.programId);
    body.append("image", file);

    const data = await this.post(this.programImageUrlValue, body);
    slot.classList.remove("animate-pulse");

    if (data?.ok) {
      this.fillSlot(slot, { id: data.attachment_id, url: data.url });
    } else {
      slot.classList.add("border-red-400");
      setTimeout(() => slot.classList.remove("border-red-400"), 3000);
    }
  }

  // ── Shared fetch ────────────────────────────────────────────────

  async post(url, body, contentType = null) {
    try {
      const headers = { "X-CSRF-Token": this.csrfValue, Accept: "application/json" };
      if (contentType) headers["Content-Type"] = contentType;
      const response = await fetch(url, { method: "POST", headers, body });
      return await response.json();
    } catch {
      return null;
    }
  }
}
